import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Star, Plus, MapPin, Calendar, Users, Home, ThumbsUp, ThumbsDown, AlertTriangle, LogIn, CheckCircle, Camera, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import GooglePlacesAutocomplete from '../components/GooglePlacesAutocomplete';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Rating input component (defined outside to avoid re-renders)
const RatingInput = ({ label, value, onChange }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-stone-600">{label}</span>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className={`p-1 ${star <= value ? 'text-amber-500' : 'text-stone-300'}`}
        >
          <Star className="w-5 h-5 fill-current" />
        </button>
      ))}
    </div>
  </div>
);

const ExperiencesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, isLoggedIn, setShowLoginModal, requireLogin } = useAuth();
  const [experiences, setExperiences] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedVenuePlace, setSelectedVenuePlace] = useState(null);
  
  const [form, setForm] = useState({
    venue_name: '',
    place_id: null,
    venue_location: '',
    date_of_visit: '',
    guest_count_planned: '',
    rooms_required: false,
    rooms_count: 0,
    rating_treatment: 3,
    rating_cleanliness: 3,
    rating_staff: 3,
    rating_transparency: 3,
    rating_parking: 3,
    liked: '',
    disliked: '',
    red_flags: '',
    would_recommend: true,
    is_verified_venue: false,
    photos: []
  });

  useEffect(() => {
    fetchExperiences();
    
    // Pre-fill venue if coming from VenueReportPage
    if (location.state?.venue_name) {
      setForm(prev => ({
        ...prev,
        venue_name: location.state.venue_name,
        place_id: location.state.place_id || null,
        is_verified_venue: !!location.state.place_id
      }));
      setShowAddModal(true);
    }
  }, [location.state]);

  const fetchExperiences = async () => {
    try {
      const response = await axios.get(`${API_URL}/experiences/all`);
      setExperiences(response.data || []);
    } catch (error) {
      console.error('Failed to fetch experiences');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVenuePlaceSelect = (placeDetails) => {
    if (placeDetails) {
      setSelectedVenuePlace(placeDetails);
      setForm(prev => ({
        ...prev,
        venue_name: placeDetails.name,
        place_id: placeDetails.place_id,
        venue_location: placeDetails.city || placeDetails.formatted_address,
        is_verified_venue: true
      }));
    } else {
      setSelectedVenuePlace(null);
      setForm(prev => ({
        ...prev,
        venue_name: '',
        place_id: null,
        venue_location: '',
        is_verified_venue: false
      }));
    }
  };

  const handleManualVenueEntry = (text) => {
    setSelectedVenuePlace(null);
    setForm(prev => ({
      ...prev,
      venue_name: text,
      place_id: null,
      is_verified_venue: false
    }));
  };

  const handleSubmit = async () => {
    if (!isLoggedIn) {
      requireLogin('submit your experience', () => setShowAddModal(true));
      return;
    }
    
    if (!form.venue_name.trim()) {
      toast.error('Please select or enter a venue name');

      return;
    }

    try {
      const params = new URLSearchParams({
        venue_name: form.venue_name,
        date_of_visit: form.date_of_visit,
        guest_count_planned: form.guest_count_planned,
        rooms_required: form.rooms_required,
        rooms_count: form.rooms_count,
        rating_treatment: form.rating_treatment,
        rating_cleanliness: form.rating_cleanliness,
        rating_staff: form.rating_staff,
        rating_transparency: form.rating_transparency,
        rating_parking: form.rating_parking,
        liked: form.liked,
        disliked: form.disliked,
        red_flags: form.red_flags,
        would_recommend: form.would_recommend,
        token: token
      });
      
      await axios.post(`${API_URL}/experiences/submit?${params.toString()}`);
      toast.success('Experience submitted! Thank you for sharing.');
      setShowAddModal(false);
      setForm({
        venue_name: '', date_of_visit: '', guest_count_planned: '',
        rooms_required: false, rooms_count: 0,
        rating_treatment: 3, rating_cleanliness: 3, rating_staff: 3,
        rating_transparency: 3, rating_parking: 3,
        liked: '', disliked: '', red_flags: '', would_recommend: true
      });
      fetchExperiences();
    } catch (error) {
      toast.error('Failed to submit experience');
    }
  };

  const getAverageRating = (ratings) => {
    const values = Object.values(ratings);
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <header className="sticky top-0 z-50 bg-[#FDFCF8]/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-serif text-xl font-medium text-stone-900">Venue Visit Reports</h1>
              <p className="text-sm text-stone-500">Real feedback from couples who visited venues</p>
            </div>
          </div>
          <Button 
            onClick={() => {
              if (isLoggedIn) {
                setShowAddModal(true);
              } else {
                requireLogin('add your visit report', () => setShowAddModal(true));
              }
            }}
            className="bg-stone-900 hover:bg-stone-800"
            data-testid="add-experience-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Visit Report
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Venue Intelligence Disclaimer */}
        <div className="flex items-start gap-3 p-4 bg-stone-50 border border-stone-200 rounded-xl mb-6">
          <Info className="w-5 h-5 text-stone-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-stone-500 leading-relaxed">
            These reports are shared by real visitors to help you understand venues before reaching out. We recommend contacting the venue directly to validate availability, pricing, and current services before making decisions.
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin mx-auto"></div>
          </div>
        ) : experiences.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-stone-200">
            <Star className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-700 mb-2">No visit reports shared yet</h3>
            <p className="text-stone-500 mb-6">Be the first to share your venue visit report!</p>
            <Button 
              onClick={() => {
                if (isLoggedIn) {
                  setShowAddModal(true);
                } else {
                  requireLogin('share experience', () => setShowAddModal(true));
                }
              }} 
              className="bg-stone-900 hover:bg-stone-800"
            >
              Share Your Experience
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {experiences.map((exp) => (
              <div key={exp.id} className="bg-white rounded-xl border border-stone-200 p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                  <div>
                    <h3 className="font-serif text-lg font-medium text-stone-900">{exp.venue_name}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-stone-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Visited: {exp.date_of_visit || 'N/A'}
                      </span>
                      {exp.guest_count_planned && (
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {exp.guest_count_planned} guests planned
                        </span>
                      )}
                      {exp.rooms_required && (
                        <span className="flex items-center gap-1">
                          <Home className="w-4 h-4" />
                          {exp.rooms_count} rooms needed
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                      {getAverageRating(exp.ratings)}/5
                    </div>
                    {exp.would_recommend ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm">
                        <ThumbsUp className="w-4 h-4" />
                        Recommends
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600 text-sm">
                        <ThumbsDown className="w-4 h-4" />
                        Does not recommend
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
                  {Object.entries(exp.ratings).map(([key, value]) => (
                    <div key={key} className="bg-stone-50 px-3 py-2 rounded-lg text-center">
                      <div className="text-xs text-stone-500 capitalize">{key}</div>
                      <div className="font-medium text-stone-900">{value}/5</div>
                    </div>
                  ))}
                </div>

                {exp.liked && (
                  <div className="mb-3">
                    <span className="text-sm font-medium text-green-700">What they liked:</span>
                    <p className="text-sm text-stone-600 mt-1">{exp.liked}</p>
                  </div>
                )}
                {exp.disliked && (
                  <div className="mb-3">
                    <span className="text-sm font-medium text-red-700">What they disliked:</span>
                    <p className="text-sm text-stone-600 mt-1">{exp.disliked}</p>
                  </div>
                )}
                {exp.red_flags && (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                    <span className="text-sm font-medium text-red-700 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      Red Flags Noticed:
                    </span>
                    <p className="text-sm text-red-600 mt-1">{exp.red_flags}</p>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-stone-100 flex justify-between items-center">
                  <span className="text-xs text-stone-400">Shared by {exp.user_name}</span>
                  <span className="text-xs text-stone-400">{new Date(exp.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add Experience Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Share Your Visit Experience</DialogTitle>
            <DialogDescription>Help other couples by sharing your honest feedback</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Venue Name *</label>
              <GooglePlacesAutocomplete
                onPlaceSelect={handleVenuePlaceSelect}
                onManualEntry={handleManualVenueEntry}
                placeholder="Search for the venue you visited..."
                showSelectedDetails={false}
                allowManualEntry={true}
              />
              {selectedVenuePlace && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  Verified Google Business: {selectedVenuePlace.city || selectedVenuePlace.formatted_address}
                </div>
              )}
              {!selectedVenuePlace && form.venue_name && (
                <div className="mt-2 flex items-center gap-2 text-sm text-amber-600">
                  <AlertTriangle className="w-4 h-4" />
                  Unverified venue - not found in Google Business
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Date of Visit</label>
                <Input 
                  type="date"
                  value={form.date_of_visit}
                  onChange={(e) => setForm(prev => ({ ...prev, date_of_visit: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">Guest Count Planned</label>
                <Input 
                  type="number"
                  placeholder="e.g., 200"
                  value={form.guest_count_planned}
                  onChange={(e) => setForm(prev => ({ ...prev, guest_count_planned: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox"
                  checked={form.rooms_required}
                  onChange={(e) => setForm(prev => ({ ...prev, rooms_required: e.target.checked }))}
                  className="rounded border-stone-300"
                />
                <span className="text-sm text-stone-700">Rooms Required</span>
              </label>
              {form.rooms_required && (
                <Input 
                  type="number"
                  placeholder="Room count"
                  value={form.rooms_count}
                  onChange={(e) => setForm(prev => ({ ...prev, rooms_count: parseInt(e.target.value) || 0 }))}
                  className="w-24"
                />
              )}
            </div>

            <div className="space-y-3 bg-stone-50 p-4 rounded-lg">
              <h4 className="font-medium text-stone-900">Ratings (1-5)</h4>
              <RatingInput 
                label="Treatment during visit" 
                value={form.rating_treatment}
                onChange={(v) => setForm(prev => ({ ...prev, rating_treatment: v }))}
              />
              <RatingInput 
                label="Cleanliness" 
                value={form.rating_cleanliness}
                onChange={(v) => setForm(prev => ({ ...prev, rating_cleanliness: v }))}
              />
              <RatingInput 
                label="Staff responsiveness" 
                value={form.rating_staff}
                onChange={(v) => setForm(prev => ({ ...prev, rating_staff: v }))}
              />
              <RatingInput 
                label="Transparency / hidden charges" 
                value={form.rating_transparency}
                onChange={(v) => setForm(prev => ({ ...prev, rating_transparency: v }))}
              />
              <RatingInput 
                label="Parking & accessibility" 
                value={form.rating_parking}
                onChange={(v) => setForm(prev => ({ ...prev, rating_parking: v }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">What you liked</label>
              <Textarea 
                placeholder="Share positive aspects..."
                value={form.liked}
                onChange={(e) => setForm(prev => ({ ...prev, liked: e.target.value }))}
                rows={2}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">What you disliked</label>
              <Textarea 
                placeholder="Share concerns..."
                value={form.disliked}
                onChange={(e) => setForm(prev => ({ ...prev, disliked: e.target.value }))}
                rows={2}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Red flags noticed</label>
              <Textarea 
                placeholder="Any warning signs..."
                value={form.red_flags}
                onChange={(e) => setForm(prev => ({ ...prev, red_flags: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio"
                  name="recommend"
                  checked={form.would_recommend}
                  onChange={() => setForm(prev => ({ ...prev, would_recommend: true }))}
                  className="border-stone-300"
                />
                <span className="text-sm text-stone-700">Would recommend</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio"
                  name="recommend"
                  checked={!form.would_recommend}
                  onChange={() => setForm(prev => ({ ...prev, would_recommend: false }))}
                  className="border-stone-300"
                />
                <span className="text-sm text-stone-700">Would not recommend</span>
              </label>
            </div>

            {/* Photo Upload Section */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Camera className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-amber-800">Add verification photos (optional)</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Upload 2-3 photos from your actual venue visit. Photos help verify your report and build trust with other couples.
                  </p>
                  <p className="text-xs text-amber-600 mt-2">
                    Note: Photos are used for verification only, not displayed publicly.
                  </p>
                  <div className="mt-3">
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-amber-300 rounded-lg cursor-pointer hover:bg-amber-50 transition-colors">
                      <Camera className="w-4 h-4 text-amber-600" />
                      <span className="text-sm text-amber-700">Select Photos</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        multiple 
                        className="hidden"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length > 3) {
                            toast.error('Maximum 3 photos allowed');
                            return;
                          }
                          // In production, upload to cloud storage
                          // For now, just store file names as placeholders
                          const fileNames = files.map(f => f.name);
                          setForm(prev => ({ ...prev, photos: fileNames }));
                          toast.success(`${files.length} photo(s) selected`);
                        }}
                      />
                    </label>
                    {form.photos && form.photos.length > 0 && (
                      <p className="text-xs text-green-600 mt-2">
                        {form.photos.length} photo(s) selected: {form.photos.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={handleSubmit} className="w-full bg-stone-900 hover:bg-stone-800">
              Submit Visit Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExperiencesPage;
